import { Strings } from "src/modules/utils/shared/strings";
import { Component, OnInit, inject,
    ChangeDetectionStrategy
} from '@angular/core';
import { Router, Routes } from '@angular/router';
import { AppMainComponent } from 'src/modules/app-template/components/app-main/app-main.component';
import { DevCustomerInfo } from 'src/modules/app-template/netgen/DevCustomerInfo';
import { DevCustomerSwitchResult } from 'src/modules/app-template/netgen/DevCustomerSwitchResult';
import { MainAppService } from 'src/modules/app-template/services/main-app.service';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FeatureModuleService } from 'src/modules/sm-base/services/feature-module.service';
import { SmBaseRoutingModule2 } from 'src/modules/sm-base/sm-base-routing2.module';
import { SmBaseModule } from 'src/modules/sm-base/sm-base.module';
import { Arrays } from 'src/modules/utils/shared/arrays';
import { Utils } from 'src/modules/utils/shared/utils';
import { PageNotFoundComponent } from "src/modules/app-template/components/page-not-found/page-not-found.component";

@Component({
    selector: 'root-component',
    standalone: false,
    templateUrl: './root.component.html',
    styleUrls: ['./root.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class RootComponent implements OnInit {
    app = inject(MainAppService);
    featureModuleService = inject(FeatureModuleService);
    router = inject(Router);

    async ngOnInit(): Promise<void> {
        let modules = this.featureModuleService.getModules();

        if (Utils.isNoe(modules)) { 
            throw new Error("In der Konfigurationsdatei wurden keine Module registriert. Ohne Module ist das Portal nicht funktionsfähig");
        }

        for (let module of modules) {
            module.registerSidebar(this.app.menuModel);
        }

        // Unter dh.<aktueller Host> leitet der Entwicklungsproxy die API an das DocumentHandler-Backend weiter
        if (this.app.mainConfig.integratedDocumentHandler && !window.location.hostname.startsWith("dh.")) {
            this.app.toolbarButtons = [...this.app.toolbarButtons,
                {
                    id: "documentHandler",
                    label: "DocumentHandler öffnen",
                    icon: "fas fa-file-code",
                    visible: true,
                    command: () => window.open(window.location.protocol + "//dh." + window.location.host + "/documenthandler/home", "_blank")
                }
            ];
        }

        if (this.app.mainConfig.devCustomerSwitchAvailable && !window.location.hostname.startsWith("dh.")) {
            this.app.toolbarButtons = [...this.app.toolbarButtons,
                {
                    id: "devCustomer",
                    label: this.app.t("general.devCustomer.title"),
                    icon: "fas fa-arrow-right-arrow-left",
                    visible: true,
                    command: async () => this.showDevCustomerMenu()
                }
            ];
        }

        if (this.app.mainConfig.useAdminUsers) {
            this.app.toolbarButtons = [...this.app.toolbarButtons,
                {
                    id: "admin",
                    label: "Administration",
                    routerLink: ["/base2", "admin"],
                    icon: "fas fa-cog",
                    visible: false
                }
            ];
        }

        this.app.menuModel = Arrays.sortBy(this.app.menuModel, item => item.sort);
        for (let tab of this.app.menuModel) {
            tab.menuItems = Arrays.sortBy(tab.menuItems, item => item.sort);
        }

        let childRoutes = modules.map(module => module.getMainRoute());
        childRoutes.push({
            path: "",
            redirectTo: Strings.def(this.app.mainConfig.mainRoute, childRoutes[0].path),
            pathMatch: "full"
        });
        childRoutes.push(this.featureModuleService.createModuleRoute("base2", () => SmBaseRoutingModule2));

        let routes: Routes = [
            {
                path: 'base',
                loadChildren: () => SmBaseModule
            },
            {
                path: '',
                component: AppMainComponent,
                children: childRoutes
            },
            { 
                path: '**', 
                component: PageNotFoundComponent 
            }
        ];

        this.router.resetConfig(routes);

        this.app.auth.onUserChanged.subscribe(async () => {
            await this.updateVisibility();
            if (this.app.mainConfig.usesChangeTracking) {
                await this.app.initChangeTracking();
            }            
        }, "RootComponent");
        await this.updateVisibility();
        if (this.app.auth.isLoggedIn() && this.app.mainConfig.usesChangeTracking) {
            await this.app.initChangeTracking();
        }     
    }

    private async showDevCustomerMenu(): Promise<void> {
        let info = await RestEndpoint.main().run("api/app/devCustomer").get(DevCustomerInfo);
        this.app.openContextMenu(info.customers.map(customer => ({
            label: customer,
            icon: customer == info.current ? "fas fa-check" : "",
            command: async () => this.switchDevCustomer(customer, info.current)
        })));
    }

    private async switchDevCustomer(customer: string, current: string): Promise<void> {
        if (customer == current) {
            return;
        }

        let [switched, result] = await this.app.saveDataHandler(() => RestEndpoint.main().body({ customer }).post().run("api/app/devCustomer").get(DevCustomerSwitchResult)) as [boolean, DevCustomerSwitchResult];
        if (!switched) {
            return;
        }
        let title = this.app.t("general.devCustomer.title");
        if (result.restarting) {
            this.app.showInfoToast(this.app.t("general.devCustomer.restarting") + " " + customer, title, 180_000);
        }
        else {
            await this.app.dialog.info(this.app.t("general.devCustomer.restartManually"), title);
        }

        // Bis das neu gestartete Backend den gewählten Kunden meldet, schlagen die Abfragen fehl oder liefern noch den bisherigen
        // Ein Neustart über Visual Studio baut vorher neu und dauert daher länger als ein direkter Neustart
        let deadline = Date.now() + (result.restarting ? 180_000 : 600_000);
        while (Date.now() < deadline) {
            await Utils.sleep(1000);
            try {
                let info = await RestEndpoint.main().run("api/app/devCustomer").get(DevCustomerInfo);
                if (info.current == customer) {
                    this.app.reloadPage();
                    return;
                }
            }
            catch {
                // Backend noch nicht wieder erreichbar
            }
        }
        this.app.showErrorToast(this.app.t("general.devCustomer.timeout"), title);
    }

    private async updateVisibility(): Promise<void> {
        for (let module of this.featureModuleService.getModules()) {
            await module.updateSidebarVisibility(this.app.menuModel);
        }
        this.app.updateMenuVisibility();
        let b = this.app.toolbarButtons.find(tb => tb.id == "admin");
        if (b != null) {
            b.visible = this.app.isAdmin();
        }
    }
}